import type { Institution, UserRoles, Coach } from '@backrs_ui/common-ui';

import { UserProfileResponse } from '../profile/profile';

export type InstitutionMembership = Institution &
  Readonly<{
    relationship_type?: string;
  }>;

export type CoachSummary = Coach &
  Readonly<{
    is_institution_member?: boolean;
    /** Holds the Backrs Coach role. Independent of `is_institution_member`: a
     * Backrs coach may also hold a partner membership. */
    is_backrs_coach?: boolean;
  }>;

export const ROLE_GROUP = {
  STUDENTS: 'students',
  COACHES: 'coaches',
  STAFF: 'staff',
} as const;

export type RoleGroup = (typeof ROLE_GROUP)[keyof typeof ROLE_GROUP];

export type AssessmentInfo = Readonly<{
  completed: boolean;
  completed_at?: string;
  completion_percentage?: number;
}>;

export type TrackAssessmentStatus = 'not_attempted' | 'unscored' | 'completed';

export type TrackAssessmentInfo = AssessmentInfo &
  Readonly<{
    status?: TrackAssessmentStatus;
    /** False when the student's enrollment makes them ineligible to take it */
    eligible?: boolean;
  }>;

export type MemberAssessments = Readonly<{
  blueprint?: AssessmentInfo;
  life_readiness?: AssessmentInfo;
  track_assessment?: TrackAssessmentInfo;
}>;

export type MemberCohort = Readonly<{ id: number; name: string | null }>;

/**
 * One seat a member holds, as the two roster reads carry it. Only seats on rosters the
 * caller's operator context reaches are sent, so the list IS the reachable set — a member
 * seated somewhere out of reach arrives with that seat absent, not marked.
 *
 * A roster has no name of its own: `roster_name` comes from the offering, so two seats
 * under different operators can carry the same one. `managing_institution` is what tells
 * them apart.
 */
export type MemberSeat = Readonly<{
  seat_id: number;
  roster_id: number;
  roster_name: string | null;
  managing_institution: Readonly<{ id: number; name: string | null }> | null;
  offering_kind: string | null;
  status: string | null;
}>;

/**
 * Absent and empty are different answers about a member's seats. A read that did not carry
 * them cannot say the member holds none — so the two are kept apart HERE, at the shape both
 * roster reads decode into, rather than in each renderer. A distinction split across
 * renderers is one consolidation away from becoming a falsy check.
 */
export type SeatSet =
  | Readonly<{ known: true; seats: readonly MemberSeat[] }>
  | Readonly<{ known: false }>;

export const decodeSeatSet = (value: unknown): SeatSet =>
  Array.isArray(value) ? { known: true, seats: value as MemberSeat[] } : { known: false };

export type Assignee = Readonly<{
  id: number;
  first_name: string | null;
  last_name: string | null;
  profile_image: string | null;
  grade: string | null;
  school: string | null;
  email: string | null;
  phone: string | null;
  coaches: CoachSummary[];
  roles?: UserRoles[];
  profile?: UserProfileResponse['profile'];
  institutions?: InstitutionMembership[];
  assessments?: MemberAssessments;
  last_login?: string | null;
  student_count?: number;
  is_verified?: boolean;
  seats: SeatSet;
  /** Absent when the read did not carry cohorts, which is not the same as none. */
  cohorts?: readonly MemberCohort[];
}>;

export type InstitutionMember = Readonly<{
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string;
  phone: string | null;
  grade: string | null;
  profile_image: string | null;
  school: string | null;
  coaches: CoachSummary[];
  roles?: UserRoles[];
  profile?: UserProfileResponse['profile'];
  institutions?: InstitutionMembership[];
  assessments?: MemberAssessments;
  last_login?: string | null;
  student_count?: number;
  is_verified?: boolean;
  seats: SeatSet;
  /** Absent when the read did not carry cohorts, which is not the same as none. */
  cohorts?: readonly MemberCohort[];
}>;

export type UpdateMemberCoachesRequest = Readonly<{
  userId: number;
  coachIds: number[];
}>;

export type BulkAssignCoachesRequest = Readonly<{
  userIds: number[];
  coachIds: number[];
}>;

export type BulkAssignCoachesResponse = Readonly<{
  success: boolean;
  assignments: readonly { id: number; super_coach_id: number; assignee_id: number }[];
  alreadyAssigned: readonly { super_coach_id: number; assignee_id: number }[];
  skippedCoachIds: number[];
  skippedUserIds: number[];
}>;

export type PaginationParams = Readonly<{
  range: [number, number];
}>;

export type CoachingDashboardFilter = Readonly<{
  q?: string;
  grades?: string[];
  schools?: string[];
  role_group?: RoleGroup;
  role_ids?: number[];
  institution_ids?: number[];
  institution_roles?: string[];
  offering_codes?: string[];
  is_verified?: boolean;
  cohort_id?: number[];
  without_cohort?: boolean;
  coach_ids?: number[];
  /** Seated on that cohort's roster and not already in it. */
  eligible_cohort_id?: number;
}>;

export type FilterRole = Readonly<{
  id: number;
  name: string;
}>;

export type FilterInstitution = Readonly<{
  id: number;
  name: string;
}>;

export type FilterGrade = Readonly<{
  value: string;
  label: string;
}>;

export type FilterProgram = Readonly<{
  code: string;
  name: string;
}>;

export type FilterCohort = Readonly<{
  id: number;
  name: string;
  institution_id: number;
}>;

export type CoachingDashboardFiltersResponse = Readonly<{
  grades: FilterGrade[];
  schools: string[];
  roles: FilterRole[];
  institutions?: FilterInstitution[];
  institution_roles?: string[];
  programs?: FilterProgram[];
  cohorts?: FilterCohort[];
  /** The role ids each group selects on, keyed by group name */
  role_group_ids?: Partial<Record<RoleGroup, number[]>>;
}>;

export type CoachingDashboardSortField = 'last_name' | 'grade' | 'last_login' | 'student_count';

export type CoachingDashboardSort = Readonly<{
  field: CoachingDashboardSortField;
  direction: 'ASC' | 'DESC';
}>;

export type CoachingDashboardQueryParams = Readonly<{
  range: [number, number];
  filter?: CoachingDashboardFilter;
  sort?: CoachingDashboardSort;
}>;

export type InstitutionMemberParams = Readonly<{
  userId: number;
}>;

export type InstitutionCoachesParams = Readonly<{
  q?: string;
  /** Narrows to coaches these institutions manage. Checked against the operator context. */
  institutionIds?: readonly number[];
}>;

type SessionRecordingStatus =
  | 'pending'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'no_recording'
  | 'no_transcript';

export type SessionRecording = Readonly<{
  id: number;
  status: SessionRecordingStatus;
  duration: number | null;
  zoom_meeting_id: string | null;
  has_transcript: boolean;
}>;

export type PastSession = Readonly<{
  id: number;
  scheduled_at: string;
  scheduled_end: string;
  invitee_name: string;
  product: Readonly<{ id: number; name: string }>;
  recording: SessionRecording | null;
}>;

export type AssigneeSessionsQueryParams = Readonly<{
  userId: number;
  range: [number, number];
}>;

export type TranscriptCue = Readonly<{
  speaker: string;
  text: string;
  startTime: string;
  endTime: string;
}>;

/** Serialized Lexical `EditorState` — stored verbatim in `body`. */
export type NoteBody = Record<string, unknown>;

export type NoteAttachment = Readonly<{
  id: number;
  name: string;
  size: number;
  is_inline: boolean;
  uri: string;
}>;

export type NoteAuthor = Readonly<{
  id: number;
  first_name: string | null;
  last_name: string | null;
  profile_image_url: string | null;
}>;

export type AssigneeNote = Readonly<{
  id: number;
  author_id: number;
  subject_id: number;
  body: NoteBody;
  body_plaintext: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  attachments?: NoteAttachment[];
  author: NoteAuthor | null;
}>;

export type AssigneeNotesQueryParams = Readonly<{
  userId: number;
  q?: string;
}>;

export type CreateAssigneeNoteRequest = Readonly<{
  userId: number;
  body: NoteBody;
  body_plaintext: string;
  attachment_ids: number[];
}>;

export type UpdateAssigneeNoteRequest = Readonly<{
  userId: number;
  noteId: number;
  body: NoteBody;
  body_plaintext: string;
  attachment_ids: number[];
}>;

export type DeleteAssigneeNoteRequest = Readonly<{
  userId: number;
  noteId: number;
}>;

export type ExportReportParams = Readonly<{
  reportKey: string;
}>;

export type ExportDownload =
  | Readonly<{ kind: 'blob'; blob: Blob; filename: string }>
  | Readonly<{ kind: 'url'; url: string; filename: string; rowCount: number }>;

export type ExportUrlResponse = Readonly<{
  filename: string;
  download_url: string;
  row_count: number;
}>;
